<template>
  <v-container fluid>
    <PaginationController v-model="orders" getDataActionName="getAppleOrders" 
      :historyLimit="historyLimit" :actionPredicates="predicates" :haveSearchBox="true"
      :searchBoxLabel="`${$t('orderId')} / Product Name / Tracking`" :searchBoxHint="$t('orderSearch')"
      :isRefreshSelect="false" :haveStartDate="true"
      keepKeywordCase ref="getAppleOrders">
      <template v-slot:dataTable="{ isLoadedToEnd }">
        <v-data-table :headers="headers" :search="filter" :items="orders"
          :pagination.sync="pagination">
          <template v-slot:items="props">
            <tr :class="props.item.abnormalOrg ? 'abnormal_list' : ''">
            <td class="text-xs-center">{{ props.index + 1 }}</td>
            <td class="text-xs-left" >
              <a linkify @click="jump(props.item.url)">{{  props.item.orderId }}</a>
            </td>
            <td class="text-xs-left">
              {{ props.item.tracking || '-' }}
            </td>
            <td class="text-xs-left">
              <v-flex row v-for="(item, index) in props.item.orderItems" :key="index + item.productName">
                <ProductWidget :name="item.productName" :quantityShipped="item.quantity">
                </ProductWidget>
              </v-flex>
            </td>
            <td class="text-xs-left">
              <v-layout column>
                <v-flex>{{ $t(props.item.status) }}</v-flex>
                <v-flex>{{ props.item.deliveryDate }}</v-flex>
              </v-layout>
            </td>
            <td class="text-xs-left">{{ props.item.createTime && toTimestampString(props.item.createTime) }}</td>
            <td class="text-xs-left">{{ getOrgId(props.item.organizationKey) }}</td>
            <td class="text-xs-left">
              {{ props.item.processStatus }}
            </td>
            <td class="text-xs-center">
              <v-layout justify-center no-wrap>
                <v-btn icon flat dark color="primary" @click.stop="deleteOrder(props.item, props.index)" :loading="props.item.loading">
                  <v-icon>delete</v-icon>
                </v-btn>
              </v-layout>
            </td>
          </tr>
          </template>
        </v-data-table>
      </template>

      <template v-slot:beforeSearchBox>
        <v-flex md4 mr-1 v-if="isWarehouse">
          <SelectWidget
            :label="$t('selectOrganization')"
            :items="organizations"
            v-model="selectedOrganization"
            chipText='organizationId'
            itemText="organizationId"
            itemValue="key"/>
        </v-flex>
        <v-flex md4>
          <v-select :label="$t('selectStatus')" :items="orderStatusList" v-model="selectedStatus"
            chipText='displayName' itemText="displayName" itemValue="key" />
        </v-flex>
      </template>
      <template v-slot:afterDate>
        <v-flex md4>
          <v-btn
            small color="primary" @click="isShowDownloadDialog = true" :disabled="loading">
            {{ $t('download') }}
          </v-btn>
        </v-flex>
        <v-flex md4>
          <v-btn v-if="isWarehouse" @click="isShowWarehouseUploadDialog = true" small color="primary" title="importFile" :loading="loading">
            <v-icon dark left small>cloud_upload</v-icon>
            {{ $t('batchUpload') }}
          </v-btn>
          <v-btn v-else small color="primary" title="importFile" :loading="loading">
            <v-icon dark left small>cloud_upload</v-icon>
            {{ $t('batchUpload') }}
            <input v-on:click="clearSelectionCache" type="file" class="hideInput" @change="uploadFile"
              accept=".csv,.xls,.xlsx" />
          </v-btn>
        </v-flex>
        <v-flex md4>
          <v-tooltip top>
            <template v-slot:activator="tooltip">
              <div v-on="tooltip.on">
                <v-btn small color="primary" title="importFile" :loading="loading" @click="batchRetryPendingOrder">
                  <v-icon dark left small>refresh</v-icon>
                  {{ $t('batchRetry') }}
                </v-btn>
              </div>
            </template>
            {{ $t('batchRetryDesc') }}
          </v-tooltip>
        </v-flex>
      </template>
    </PaginationController>
    <FormSubmitPopup
      v-model="isShowDownloadDialog"
      @popupClose="isShowDownloadDialog = false"
      :title="$t('download')"
      v-if="isShowDownloadDialog"
      :rightMethod="downloadAppleOrders"
      :rightButtonText="$t('submit')"
    >
      <template v-slot:input>
        <v-container>
          <v-layout v-if="isWarehouse">
            <v-flex>
              <v-select
                :label="$t('organizationName')"
                v-model="selectedDownloadOrganization"
                :items="organizations"
                item-text="organizationId"
                item-value="key"
                clearable
                chips
                small
              ></v-select>
            </v-flex>
          </v-layout>
          <v-layout mb-3>
            <v-flex>
              <DatePicker
              v-model="downloadParams.startDate"
              :dateString="true"
              :label="$t('startDate')"
              :isShowIcon="false"
              ></DatePicker>
            </v-flex>
            <v-flex>
              <DatePicker
              v-model="downloadParams.endDate"
              :dateString="true"
              :label="$t('endDate')"
              :isShowIcon="false"
              ></DatePicker>
            </v-flex>
          </v-layout>
        </v-container>
      </template>

    </FormSubmitPopup>
    <FormSubmitPopup
      v-model="showAlreadyExistsOrdersDialog"
      @popupClose="showAlreadyExistsOrdersDialog = false"
      :title="$t('alreadyExistsOrders')"
      v-if="showAlreadyExistsOrdersDialog"
      hideRgtBtn
    >
      <template v-slot:input>
        <v-container>
          <v-layout mb-3 align-center>
            <v-flex>
              <v-textarea
                id="alreadyExistsOrders"
                :label="$t('alreadyExistsOrders')"
                outline
                v-model="alreadyExistsOrders"
                readonly
                class="thinBox">
              </v-textarea>
            </v-flex>
          </v-layout>
        </v-container>
      </template>

    </FormSubmitPopup>
    <FormSubmitPopup
      v-model="isShowWarehouseUploadDialog"
      @popupClose="isShowWarehouseUploadDialog = false"
      :title="$t('batchUpload')"
      v-if="isShowWarehouseUploadDialog"
      hideRgtBtn
    >
      <template v-slot:input>
        <v-container>
          <v-layout mb-3 align-center>
            <v-flex>
              <v-select
                :label="$t('organizationName')"
                v-model="selectOrganizationUpload"
                :items="organizations"
                item-text="organizationId"
                item-value="key">
              </v-select>
            </v-flex>
            <v-flex>
              <v-btn :disabled="!selectOrganizationUpload" small color="primary" title="importFile" :loading="loading">
                <v-icon dark left small>cloud_upload</v-icon>
                {{ $t('batchUpload') }}
                <input v-on:click="clearSelectionCache" type="file" class="hideInput" @change="uploadFile"
                  accept=".csv,.xls,.xlsx" />
              </v-btn>
            </v-flex>
          </v-layout>
        </v-container>
      </template>

    </FormSubmitPopup>
  </v-container>
</template>

<script>

import PaginationController from './PaginationController'
import UploadFileWidget from './UploadFileWidget'
import { timeTools, checkRules, measurementTools, readXlsxSheetData, exportListToXlsx, toDateEnd, toDateStart } from '../utils/tools'
import ProductWidget from './ProductWidget'
import SelectWidget from './SelectWidget'
import FormSubmitPopup from './FormSubmitPopup'
import DatePicker from './DatePicker'

export default {
  name: 'AppleOrders',
  components: {
    PaginationController,
    UploadFileWidget,
    SelectWidget,
    ProductWidget,
    FormSubmitPopup,
    DatePicker
  },
  data () {
    return {
      filter: '',
      pagination: {
        sortBy: '',
        rowsPerPage: -1
      },
      loading: false,
      archivedShipments: [],
      historyLimit: 50,
      rawOrders: '',
      uploadedFiles: [],
      errorUrlIndexList: [],
      currentRevisePkgOrder: {},
      addedOrders: [],
      orderStatusList: [
        {
          displayName: this.$t('all'),
          key: ''
        },
        {
          displayName: this.$t('canceled'),
          key: 'canceled'
        },
        {
          displayName: this.$t('outForDelivery'),
          key: 'outForDelivery'
        },
        {
          displayName: this.$t('returned'),
          key: 'returned'
        },
        {
          displayName: this.$t('pending'),
          key: 'pending'
        },
        {
          displayName: this.$t('arrival'),
          key: 'arrival'
        },
        {
          displayName: this.$t('inStock'),
          key: 'inStock'
        },
        {
          displayName: this.$t('finished'),
          key: 'finished'
        }
      ],
      orders: [],
      selectedStatus: '',
      isShowDownloadDialog: false,
      downloadParams: {
        startDate: new Date(),
        endDate: new Date()
      },
      selectedOrganization: [],
      selectOrganizationUpload: '',
      selectedDownloadOrganization: '',
      showAlreadyExistsOrdersDialog: false,
      isShowWarehouseUploadDialog: false
    }
  },
  mixins: [timeTools, checkRules, measurementTools],
  computed: {
    headers () {
      return [
        { text: '#', sortable: false, value: '', width: 30 },
        { text: this.$t('orderId'), value: 'orderId', align: 'left', sortable: true, width: '15%' },
        { text: this.$t('tracking'), value: 'isExpedited', align: 'left', sortable: false, width: '15%' },
        { text: this.$t('orderDetail'), value: 'orderDetail', align: 'left', sortable: false, width: '25%' },
        { text: this.$t('status'), value: 'status', align: 'left', sortable: false, width: '25%' },
        { text: this.$t('createTime'), value: 'createTime', align: 'left', sortable: true, width: '15%' },
        { text: this.$t('organization'), value: 'orgId', align: 'left', sortable: true, width: '10%' },
        { text: this.$t('processStatus'), value: 'processStatus', align: 'left', sortable: true, width: '15%' },
        { text: this.$t('actions'), value: 'actions', align: 'center', sortable: false, width: '30%' }
      ]
    },
    predicates () {
      let predicates = []
      if (this.selectedStatus) {
        predicates.push({ field: 'status', compare: '==', value: this.selectedStatus })
      }
      if (this.selectedOrganization.length && this.selectedOrganization.length !== this.organizations.length) {
        predicates.push({ field: 'organizationKey', compare: 'in', value: this.selectedOrganization })
      }
      return predicates
    },
    isWarehouse () {
      return this.$store.getters.activeWarehouse
    },
    organizations () {
      return this.$store.getters.warehouseOrganizations.filter(element => {
        return element.organizationId !== '' && element.organizationId !== 'unknown'
      })
    }
  },
  mounted () {
    this.init()
  },
  methods: {
    clearSelectionCache (e) {
      e.target.value = null
    },
    async uploadFile (e) {
      this.loading = true
      try {
        const files = e.target.files
        let rawData = await readXlsxSheetData(files, '', {parseLink: true})
        rawData = rawData.map((data, index) => {
          let { url } = data
          if (url) {
            let regExp = /W\d{10}/
            let matchOrderId = url?.match(regExp)
            let orderId = matchOrderId ? matchOrderId[0] : ''
            if (!orderId) {
              this.errorUrlIndexList.push(index + 2)
            }
            data.orderId = orderId
            return {url, orderId}
          }
        })
        if (this.errorUrlIndexList.length) {
          this.$store.dispatch('showToast', { info: this.$t('errorUrlList') + this.errorUrlIndexList.join(','), level: 'error' })
          this.loading = false
        }
        rawData = rawData.filter(data => data && data.orderId)
        if (!rawData.length) {
          this.$store.dispatch('showToast', { info: this.$t('noData'), level: 'error' })
          this.loading = false
          return
        }
        let payload = { dataList: rawData, actionType: 'upload' }
        payload.organizationKey = this.selectOrganizationUpload
        let result = await this.$store.dispatch('uploadAppleOrders', payload)
        const {alreadyExistsOrders = [], addedOrdersList = []} = result.data
        addedOrdersList.forEach(item => {
          const {orderId} = item
          let matchedOrder = this.orders.find(order => order.orderId === orderId)
          if (matchedOrder) {
            this.$set(matchedOrder, 'processStatus', 'pending')
          } else {
            this.orders.unshift(item)
          }
          this.isShowWarehouseUploadDialog = false
        })
        if (alreadyExistsOrders.length) {
          this.showAlreadyExistsOrdersDialog = true
          this.alreadyExistsOrders = alreadyExistsOrders.join('\n')
        }
        this.loading = false
      } catch (error) {
        this.loading = false
        this.$store.dispatch('showToast', { info: error })
      }
    },
    
    async batchRetryPendingOrder () {
      this.loading = true
      try {
        let result = await this.$store.dispatch('uploadAppleOrders', { actionType: 'retry' })
        this.loading = false
        this.orders.forEach(order => {
          if (result.data?.processOrderIdList.includes(order.orderId)) {
            order.processStatus = 'pending'
          }
        })
      } catch (error) {
        this.loading = false
        this.$store.dispatch('showToast', { info: error })
      }
    },
    async deleteOrder (item, index) {
      if (!confirm(this.$t('confirmDelete', { name: this.$t('order') }))) return Promise.resolve()
      this.$set(item, 'loading', true)
      return this.$store.dispatch('deleteAppleOrder', { key: item._key }).then(res => {
        this.$set(item, 'loading', false)
        this.orders.splice(index, 1)
      })
    },
    isMoreThan7DaysApart (date1, date2) {
      const oneDay = 1000 * 60 * 60 * 24
      const differenceInTime = Math.abs(date2.getTime() - date1.getTime())
      const differenceInDays = differenceInTime / oneDay
      return differenceInDays > 7
    },
    async downloadAppleOrders () {
      const {startDate, endDate} = this.downloadParams
      if (new Date(startDate) > new Date(endDate)) {
        this.$store.dispatch('showToast', {info: 'Error Date', level: 'error'})
        return
      }
      if (this.isMoreThan7DaysApart(toDateStart(endDate), toDateStart(startDate))) {
        this.$store.dispatch('showToast', {info: 'Maximum download 7 days data', level: 'error'})
        return
      }
      let payload = {
        startDate: toDateStart(startDate),
        endDate: toDateEnd(endDate),
        organizationKey: this.selectedDownloadOrganization
      }
      const orders = await this.$store.dispatch('getAppleOrdersByDate', payload)
      const headers = ['orderId', 'tracking', 'productName', 'quantity', 'status', 'deliveryDate', 'orgID', 'createTime']
      let data = orders.reduce((sum, item) => {
        const {orderItems} = item
        item.createTime = this.toDateMMDDHHmm(item.createTime)
        item.orgID = this.getOrgId(item.organizationKey) + (item.abnormalOrg ? ' (abnormal)' : '')
        let infoList = orderItems ? orderItems.map(info => {
          const {quantity, productName} = info
          let record = {
            ...item,
            quantity,
            productName
          }
          return headers.map(key => record[key])
        }) : [headers.map(key => item[key])]
        return [...sum, ...infoList]
      }, [])
      const filename = `appleOrders-${this.downloadParams.startDate}~${this.downloadParams.endDate}`
      exportListToXlsx(
        [
          headers,
          ...data
        ], filename
      )
    },
    jump (url) {
      window.open(url)
    },
    getOrgId (key) {
      const org = this.organizations.find(org => org.key === key)
      return org ? org.organizationId : '-'
    },
    init () {
      if (!this.isWarehouse) {
        return
      }
      const code = prompt('Please input code')
      if (code === null) {
        return this.$router.go(-1)
      }
      if (code !== 'viteusa123') {
        return this.init()
      }
      return code
    }
  }
}
</script>

<style>
.hideInput {
  position: absolute;
  top: 0;
  left: -15px;
  width: 100px;
  min-height: 100%;
  text-align: right;
  filter: alpha(opacity=0);
  opacity: 0;
  outline: none;
  cursor: inherit;
  display: block;
}
.abnormal_list {
  background-color : yellow
}
</style>



// WEBPACK FOOTER //
// src/components/AppleOrders.vue